import { forwardRef, HTMLAttributes } from 'react'
import { X } from 'lucide-react'

import { TagDto } from '@/common/models/gamification'
import { toCssColor } from '@/lib/color-format'
import { cn } from '@/lib/utils'

import { translationText } from './labels'

export interface TagChipProps extends HTMLAttributes<HTMLSpanElement> {
  tag: TagDto
  /** Renders a trailing remove (X) button; omit for read-only display. */
  onRemove?: () => void
}

/**
 * Renders a tag using its own text/background colour. `tag.textColor`/`backgroundColor` are
 * `#AARRGGBB` (alpha first, the API convention) — converted to CSS hex (alpha last) via
 * `toCssColor` before being handed to inline `style`. `toCssColor` also tolerates a value that is
 * already a plain CSS hex, so legacy rows still show their colour instead of rendering blank.
 *
 * When a colour is missing/unparseable the chip keeps a visible neutral fill (never a fully
 * transparent, invisible pill) so it still reads as a tag.
 *
 * `forwardRef` + prop spreading so callers (e.g. a `@dnd-kit` sortable chip list) can attach
 * drag handles/refs directly onto the rendered element.
 */
export const TagChip = forwardRef<HTMLSpanElement, TagChipProps>(
  ({ tag, onRemove, className, style, ...props }, ref) => {
    const label = translationText(tag.translations, 'Untitled tag')
    const textColor = toCssColor(tag.textColor)
    const backgroundColor = toCssColor(tag.backgroundColor)
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border border-monochrome-120 px-3 py-1 text-xs font-semibold leading-5',
          // Visible fallbacks so a tag with missing/unparseable colours still renders as a pill.
          !textColor && 'text-monochrome-1',
          !backgroundColor && 'bg-monochrome-5',
          className,
        )}
        style={{
          color: textColor,
          backgroundColor: backgroundColor,
          ...style,
        }}
        {...props}
      >
        {label}
        {onRemove && (
          <button
            type="button"
            aria-label={`Remove ${label}`}
            // Stop the pointer-down from being interpreted as a drag-start on the chip, and the
            // click from bubbling to any parent drag handlers.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            className="shrink-0 hover:opacity-70"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    )
  },
)
TagChip.displayName = 'TagChip'
